import { useEffect, useState } from 'react';

/**
 * The width at which the play screen becomes one panel at a time
 * (play.css, "Phone"). The same breakpoint the layout always collapsed at.
 */
export const PHONE_QUERY = '(max-width: 820px)';

function matches(): boolean {
  try {
    return window.matchMedia(PHONE_QUERY).matches;
  } catch {
    return false;
  }
}

/**
 * Whether the play screen is in its phone layout, and while it is, the size
 * of what can actually be seen.
 *
 * A phone's keyboard covers the bottom of the page without changing its
 * height in most browsers (Safari always; Chrome unless the page asks, which
 * index.html does with `interactive-widget`). A terminal sized to the page
 * would have its prompt under the keyboard, which is where the cursor is.
 * `visualViewport` is the part above the keyboard, so its height and offset
 * are written as `--vv-h` and `--vv-top` for play.css to size the frame by.
 */
export function usePhoneLayout(): boolean {
  const [phone, setPhone] = useState(matches);

  useEffect(() => {
    let query: MediaQueryList;
    try {
      query = window.matchMedia(PHONE_QUERY);
    } catch {
      return;
    }
    const update = () => setPhone(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const view = window.visualViewport;
    if (!phone || !view) return;
    const root = document.documentElement;
    const apply = () => {
      root.style.setProperty('--vv-h', `${Math.round(view.height)}px`);
      root.style.setProperty('--vv-top', `${Math.round(view.offsetTop)}px`);
    };
    apply();
    view.addEventListener('resize', apply);
    view.addEventListener('scroll', apply);
    return () => {
      view.removeEventListener('resize', apply);
      view.removeEventListener('scroll', apply);
      root.style.removeProperty('--vv-h');
      root.style.removeProperty('--vv-top');
    };
  }, [phone]);

  return phone;
}
